// ============================================================================
// USE OPERATIONS USERS HOOK
// Load users with total-operations module access for dropdowns/assignments
// Date: November 15, 2025
// ============================================================================

'use client';

import { useState, useEffect, useMemo, useCallback } from 'react';
import { authService } from '@/lib/authService';
import { isSilentFetchError, isTransientNetworkError } from '@/lib/capacitorFetchPatch';

export interface OperationsUser {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  role: string;
  module_role?: string;
  status?: string;
  is_field_worker?: boolean;
}

interface UseOperationsUsersReturn {
  users: OperationsUser[];
  fieldWorkers: OperationsUser[];
  loading: boolean;
  error: string | null;
  getUserName: (userId: number) => string;
  refreshUsers: () => Promise<void>;
}

export interface UseOperationsUsersOptions {
  moduleKey?: string;
}

/**
 * Hook to load and manage users with total-operations module access
 * Dynamically loads from module_access table based on client_id
 */
export function useOperationsUsers(options?: UseOperationsUsersOptions): UseOperationsUsersReturn {
  const resolvedModuleKey = options?.moduleKey || 'total-operations';
  const [users, setUsers] = useState<OperationsUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = authService.getTokens()?.access_token;
      if (!token) {
        throw new Error('No authentication token');
      }

      const response = await fetch(`/api/v1/module-access/users?module_key=${encodeURIComponent(resolvedModuleKey)}&include_operations_role=true`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to load users: ${response.statusText}`);
      }

      const data = await response.json();

      if (data.success) {
        setUsers(data.data || []);
      } else {
        throw new Error(data.error || 'Failed to load users');
      }
    } catch (err: unknown) {
      if (!isSilentFetchError(err)) console.error('Error loading operations users:', err);
      setError(
        isTransientNetworkError(err)
          ? 'Network unavailable'
          : err instanceof Error ? err.message : 'Failed to load users'
      );
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  // Load users on mount / when moduleKey changes
  useEffect(() => {
    loadUsers();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolvedModuleKey]);

  // Helper to format user name
  const getUserName = useCallback((userId: number): string => {
    const user = users.find(u => u.id === userId);
    if (!user) return 'Unknown';

    const name = [user.first_name, user.last_name].filter(Boolean).join(' ');
    return name || user.email;
  }, [users]);

  // Filter to field workers only (memoized to avoid new array ref each render)
  const fieldWorkers = useMemo(() => users.filter(u => u.is_field_worker), [users]);

  return {
    users,
    fieldWorkers,
    loading,
    error,
    getUserName,
    refreshUsers: loadUsers
  };
}
